import React, { createContext, useContext, useEffect, useState } from 'react'
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  updateProfile
} from 'firebase/auth'
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore'
import { auth, db, firebaseEnabled } from '../firebase'

const AuthContext = createContext(null)

const normalizeRole = (role, email) => {
  const admins = (process.env.REACT_APP_ADMIN_EMAILS || '')
    .split(',')
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean)

  if (role === 'admin' && !admins.includes(email.toLowerCase())) {
    return 'client'
  }

  return role || 'client'
}

const buildProfile = (user, profileDoc) => {
  if (profileDoc?.exists()) {
    const data = profileDoc.data()
    return {
      ...data,
      role: data.role || 'client',
      name: data.name || user.displayName || user.email?.split('@')[0] || 'Guest',
      email: data.email || user.email
    }
  }

  return {
    uid: user.uid,
    email: user.email,
    name: user.displayName || user.email?.split('@')[0] || 'Guest',
    role: 'client',
    phone: '',
    createdAt: serverTimestamp()
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  const refreshProfile = async (activeUser) => {
    if (!activeUser) {
      setProfile(null)
      return null
    }

    const profileRef = doc(db, 'users', activeUser.uid)
    const profileDoc = await getDoc(profileRef)
    const nextProfile = buildProfile(activeUser, profileDoc)

    if (!profileDoc.exists()) {
      await setDoc(profileRef, nextProfile, { merge: true })
    }

    setProfile(nextProfile)
    return nextProfile
  }

  useEffect(() => {
    if (!firebaseEnabled) {
      setUser(null)
      setProfile(null)
      setLoading(false)
      return () => {}
    }
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser)
      if (!currentUser) {
        setProfile(null)
        setLoading(false)
        return
      }

      try {
        await refreshProfile(currentUser)
      } catch (err) {
        console.error('Failed to load profile', err)
      }
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  const signUp = async ({ email, password, name, role }) => {
    if (!firebaseEnabled) {
      throw new Error('Firebase auth is not configured.')
    }
    const normalizedRole = normalizeRole(role, email)
    const credential = await createUserWithEmailAndPassword(auth, email, password)
    if (name) {
      await updateProfile(credential.user, { displayName: name })
    }

    const profileRef = doc(db, 'users', credential.user.uid)
    const profilePayload = {
      uid: credential.user.uid,
      email,
      name: name || email.split('@')[0],
      role: normalizedRole,
      phone: '',
      createdAt: serverTimestamp()
    }

    await setDoc(profileRef, profilePayload, { merge: true })
    setProfile(profilePayload)
    return profilePayload
  }

  const signIn = async (email, password) => {
    if (!firebaseEnabled) {
      throw new Error('Firebase auth is not configured.')
    }
    const credential = await signInWithEmailAndPassword(auth, email, password)
    return credential.user
  }

  const signOut = async () => {
    if (!firebaseEnabled) {
      return
    }
    await firebaseSignOut(auth)
    setProfile(null)
  }

  const updateUserProfile = async (updates) => {
    if (!firebaseEnabled) {
      throw new Error('Firebase auth is not configured.')
    }
    if (!user) return null
    const profileRef = doc(db, 'users', user.uid)
    const nextProfile = {
      ...profile,
      ...updates
    }
    await setDoc(profileRef, nextProfile, { merge: true })
    if (updates.name) {
      await updateProfile(user, { displayName: updates.name })
    }
    setProfile(nextProfile)
    return nextProfile
  }

  const value = {
    user,
    profile,
    loading,
    firebaseEnabled,
    signUp,
    signIn,
    signOut,
    refreshProfile,
    updateUserProfile
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => useContext(AuthContext)
